// Browser-safe oracle adapter. In browser, persists to localStorage under
// key "evat:oracle". In Node/Electron, callers can provide their own
// persistence, but this module avoids direct fs/path usage to stay
// compatible with Vite bundling.

export type OracleCache = {
  energy: Record<string, number>;
  carbon: Record<string, number>;
  updatedAt: string;
};

const LS_KEY = 'evat:oracle';

function load(): OracleCache {
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem(LS_KEY) : null;
    if (!raw) return { energy: {}, carbon: {}, updatedAt: new Date().toISOString() };
    return JSON.parse(raw) as OracleCache;
  } catch {
    return { energy: {}, carbon: {}, updatedAt: new Date().toISOString() };
  }
}

function save(c: OracleCache) {
  try { if (typeof window !== 'undefined') window.localStorage.setItem(LS_KEY, JSON.stringify(c)); } catch {}
}

function drift(prev: number, base: number): number {
  const p = isFinite(prev) && prev > 0 ? prev : base;
  const noise = (Math.random() - 0.5) * 0.05; // ±5%
  return Math.max(0, p * (1 + noise));
}

export async function getEnergyPrice(country: string): Promise<number> {
  const cache = load();
  const prev = cache.energy[country] ?? 0.23; // €/kWh default
  const next = parseFloat(drift(prev, 0.23).toFixed(3));
  cache.energy[country] = next;
  cache.updatedAt = new Date().toISOString();
  save(cache);
  return next;
}

export async function getCarbonMarketPrice(country: string): Promise<number> {
  const cache = load();
  const prev = cache.carbon[country] ?? 70; // €/ton default
  const next = Math.round(drift(prev, 70));
  cache.carbon[country] = next;
  cache.updatedAt = new Date().toISOString();
  save(cache);
  return next;
}



